// 階層ビュー (Hierarchical Drill-Down View)
// 任意の depth で「フォルダ → 子フォルダ → ... → 末端」を表現する汎用UI。
//
// 主な機能:
//   - 各層で rows: HierarchyNode[] を返す builder 関数を受け取る
//   - 末端 (navigable === false) のクリックは leafAction で別アクション
//   - 配下の総 count が flattenThreshold 以下になったら自動でフラット展開
//   - 階層ヘッダ右に追加アクション領域 (actionPerLevel)
//   - 末端ノード群のカスタムレンダリング (renderLeaves) も差替可

import { useState, useMemo, ReactNode } from "react";
import {
  Box, Stack, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Breadcrumbs, Link as MuiLink, Chip,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlaceIcon from "@mui/icons-material/Place";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { EmptyState } from "./EmptyState";

export type HierarchyNode<TData = unknown> = {
  key: string;
  label: string;
  count?: number;
  navigable?: boolean;
  data?: TData;
  extraMeta?: ReactNode;
  extraCells?: ReactNode[];
};

export type HierarchyLevelExtraColumn = {
  header: string;
  width?: number;
  align?: "left" | "right" | "center";
};

export type HierarchyLevel<TData = unknown> = {
  title: string;
  rows: HierarchyNode<TData>[];
  extraColumns?: HierarchyLevelExtraColumn[];
};

export type HierarchyBuilder<TData = unknown> = (path: string[]) => HierarchyLevel<TData> | null;

export type LeafRowInfo<TData = unknown> = {
  breadcrumb: string[];
  pathKeys: string[];
  node: HierarchyNode<TData>;
};

export function HierarchicalView<TData = unknown>({
  builder,
  leafAction,
  emptyText,
  flattenThreshold = 10,
  actionPerLevel,
  renderLeaves,
  initialPath,
}: {
  builder: HierarchyBuilder<TData>;
  leafAction?: (path: string[], data: TData | undefined) => void;
  emptyText?: string;
  flattenThreshold?: number;
  actionPerLevel?: (path: { key: string; label: string }[]) => ReactNode;
  renderLeaves?: (rows: LeafRowInfo<TData>[], onLeafClick: (path: string[], data: TData | undefined) => void) => ReactNode;
  initialPath?: { key: string; label: string }[];
}) {
  const [path, setPath] = useState<{ key: string; label: string }[]>(initialPath ?? []);

  const currentLevel = useMemo(() => builder(path.map((p) => p.key)), [path, builder]);

  const allLeaves = useMemo(() => {
    const out: { breadcrumb: string[]; keys: string[]; node: HierarchyNode<TData> }[] = [];
    if (!currentLevel) {
      return out;
    }
    const walk = (p: string[], breadcrumb: string[], localKeys: string[]) => {
      const lvl = builder(p);
      if (!lvl) return;
      for (const row of lvl.rows) {
        if (row.navigable === false) {
          out.push({ breadcrumb, keys: localKeys, node: row });
        } else {
          walk([...p, row.key], [...breadcrumb, row.label], [...localKeys, row.key]);
        }
      }
    };
    walk(path.map((p) => p.key), [], []);
    return out;
  }, [currentLevel, builder, path]);

  const totalCount = currentLevel?.rows.reduce((s, r) => s + (r.count ?? 0), 0) ?? 0;
  const allLeavesAlready = currentLevel?.rows.every((r) => r.navigable === false) ?? false;
  const flattenMode = !!currentLevel && !allLeavesAlready && totalCount > 0 && totalCount <= flattenThreshold;

  return (
    <Stack gap={2}>
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, backgroundColor: "grey.50" }}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ flex: 1 }}>
            <MuiLink component="button" underline={path.length > 0 ? "hover" : "none"}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: path.length > 0 ? "pointer" : "default" }}
              color={path.length === 0 ? "text.primary" : "primary.main"}
              disabled={path.length === 0}
              onClick={() => setPath([])}>
              <HomeIcon fontSize="small" /> ルート
            </MuiLink>
            {path.map((p, i) => {
              const isLast = i === path.length - 1;
              return (
                <MuiLink key={p.key} component="button" underline={isLast ? "none" : "hover"}
                  sx={{ cursor: isLast ? "default" : "pointer" }}
                  color={isLast ? "text.primary" : "primary.main"}
                  disabled={isLast}
                  onClick={() => setPath(path.slice(0, i + 1))}>
                  {p.label}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
          {actionPerLevel && <Box>{actionPerLevel(path)}</Box>}
        </Stack>
        {flattenMode && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            ⓘ 配下が {totalCount} 件のためフラット展開で表示しています
          </Typography>
        )}
      </Paper>

      {!currentLevel ? (
        <EmptyState message={emptyText ?? "これ以上の階層がありません"} />
      ) : currentLevel.rows.length === 0 ? (
        <EmptyState message={emptyText ?? "該当するデータがありません"} />
      ) : flattenMode ? (
        renderLeaves
          ? renderLeaves(
              allLeaves.map((l) => ({
                breadcrumb: [...path.map((p) => p.label), ...l.breadcrumb],
                pathKeys: [...path.map((p) => p.key), ...l.keys],
                node: l.node,
              })),
              (pathArr, data) => leafAction?.(pathArr, data),
            )
          : <FlatLeavesTable leaves={allLeaves} leafAction={leafAction} pathKeys={path.map((p) => p.key)} />
      ) : allLeavesAlready && renderLeaves ? (
        renderLeaves(
          currentLevel.rows.map((row) => ({
            breadcrumb: path.map((p) => p.label),
            pathKeys: path.map((p) => p.key),
            node: row,
          })),
          (pathArr, data) => leafAction?.(pathArr, data),
        )
      ) : (
        <LevelTable
          level={currentLevel}
          onNavigate={(row) => setPath([...path, { key: row.key, label: row.label }])}
          onLeaf={(row) => leafAction?.([...path.map((p) => p.key), row.key], row.data)}
        />
      )}
    </Stack>
  );
}

function LevelTable<TData>({ level, onNavigate, onLeaf }: {
  level: HierarchyLevel<TData>;
  onNavigate: (row: HierarchyNode<TData>) => void;
  onLeaf: (row: HierarchyNode<TData>) => void;
}) {
  const hasExtraMeta = level.rows.some((r) => r.extraMeta != null);
  const extraColumns = level.extraColumns ?? [];
  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", width: 8, p: 0 }} />
            <TableCell sx={{ fontWeight: "bold", width: 50, py: 1 }} />
            <TableCell sx={{ fontWeight: "bold" }}>{level.title}</TableCell>
            {hasExtraMeta && (
              <TableCell sx={{ fontWeight: "bold", width: 220 }}>工程進捗</TableCell>
            )}
            {extraColumns.map((col, ci) => (
              <TableCell key={ci} sx={{ fontWeight: "bold", width: col.width ?? 90 }} align={col.align ?? "right"}>
                {col.header}
              </TableCell>
            ))}
            <TableCell sx={{ fontWeight: "bold", width: 140 }} align="right">配下件数 / 操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {level.rows.map((row) => {
            const isLeaf = row.navigable === false;
            const hasChildren = row.count == null || row.count > 0;
            const disabled = !isLeaf && !hasChildren;
            return (
              <TableRow key={row.key} hover={!disabled}
                sx={{
                  cursor: disabled ? "default" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                  borderLeft: 4,
                  borderLeftColor: isLeaf ? "success.main" : "primary.main",
                  bgcolor: isLeaf ? "rgba(46,125,50,0.04)" : "background.paper",
                  "&:hover": {
                    bgcolor: isLeaf ? "rgba(46,125,50,0.10)" : "rgba(25,118,210,0.06)",
                  },
                }}
                onClick={() => {
                  if (disabled) return;
                  if (isLeaf) onLeaf(row);
                  else onNavigate(row);
                }}>
                <TableCell sx={{ p: 0 }} />
                <TableCell sx={{ py: 1, color: isLeaf ? "success.main" : "primary.main" }}>
                  {isLeaf
                    ? <PlaceIcon fontSize="small" />
                    : <FolderOpenIcon fontSize="small" />}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={isLeaf ? 400 : 600}
                    color={isLeaf ? "success.dark" : "text.primary"}>
                    {row.label}
                  </Typography>
                </TableCell>
                {hasExtraMeta && (
                  <TableCell sx={{ py: 0.5 }}>{row.extraMeta ?? null}</TableCell>
                )}
                {extraColumns.map((col, ci) => (
                  <TableCell key={ci} sx={{ py: 0.5 }} align={col.align ?? "right"}>
                    {row.extraCells?.[ci] ?? null}
                  </TableCell>
                ))}
                <TableCell align="right">
                  {isLeaf ? (
                    <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end" color="success.main">
                      <Typography variant="caption" fontWeight={600}>選択</Typography>
                      <ArrowForwardIcon fontSize="small" />
                    </Stack>
                  ) : (
                    <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end">
                      <Chip size="small" color={row.count && row.count > 0 ? "primary" : "default"} variant="outlined"
                        label={row.count != null ? `${row.count} 件` : "—"} />
                      <ChevronRightIcon fontSize="small" sx={{ color: "primary.main" }} />
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}

function FlatLeavesTable<TData>({ leaves, leafAction, pathKeys }: {
  leaves: { breadcrumb: string[]; keys: string[]; node: HierarchyNode<TData> }[];
  leafAction?: (path: string[], data: TData | undefined) => void;
  pathKeys: string[];
}) {
  if (leaves.length === 0) {
    return <EmptyState message="該当する末端ノードがありません" />;
  }
  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 8, p: 0 }} />
            <TableCell sx={{ width: 50, fontWeight: "bold" }} />
            <TableCell sx={{ fontWeight: "bold", width: "40%" }}>パス</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>項目</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 100 }} align="right">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leaves.map(({ breadcrumb, keys, node }) => (
            <TableRow key={`${keys.join("/")}/${node.key}`} hover
              sx={{
                cursor: leafAction ? "pointer" : "default",
                borderLeft: 4,
                borderLeftColor: "success.main",
                bgcolor: "rgba(46,125,50,0.04)",
                "&:hover": { bgcolor: "rgba(46,125,50,0.10)" },
              }}
              onClick={() => leafAction?.([...pathKeys, ...keys, node.key], node.data)}>
              <TableCell sx={{ p: 0 }} />
              <TableCell sx={{ py: 1, color: "success.main" }}>
                <PlaceIcon fontSize="small" />
              </TableCell>
              <TableCell>
                <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                  {breadcrumb.map((b, j) => (
                    <span key={j} style={{ display: "inline-flex", alignItems: "center" }}>
                      <Typography variant="caption" color="text.secondary">{b}</Typography>
                      {j < breadcrumb.length - 1 && <NavigateNextIcon fontSize="small" sx={{ color: "text.disabled", mx: 0.25 }} />}
                    </span>
                  ))}
                </Stack>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="success.dark">{node.label}</Typography>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end" color="success.main">
                  <Typography variant="caption" fontWeight={600}>選択</Typography>
                  <ArrowForwardIcon fontSize="small" />
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
